import React, { useState, useCallback, useEffect } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { fmtDateTime } from '../../../../utils/ruDates';

function OzonDescriptionHistory({ accountId }) {
    const { request, showStatus } = useApi();

    const [batches, setBatches] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const [expandedBatch, setExpandedBatch] = useState(null);
    const [batchDetail, setBatchDetail] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);

    const [taskResult, setTaskResult] = useState(null);
    const [reverting, setReverting] = useState(null);
    const [exporting, setExporting] = useState(false);

    // Cell expand modal
    const [cellModal, setCellModal] = useState(null);

    const loadHistory = useCallback(async () => {
        if (!accountId) return;
        setLoading(true);
        try {
            const data = await request(`/api/v1/ozon-desc/${accountId}/history?page=${page}&page_size=20`);
            setBatches(data.batches || []);
            setTotal(data.total || 0);
        } catch (err) {
            showStatus('error', `Ошибка загрузки истории: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [accountId, page, request, showStatus]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const loadBatchDetail = useCallback(async (batchId) => {
        if (expandedBatch === batchId) {
            setExpandedBatch(null);
            return;
        }
        setExpandedBatch(batchId);
        setDetailLoading(true);
        try {
            const data = await request(`/api/v1/ozon-desc/${accountId}/batch/${batchId}`);
            setBatchDetail(data.items || []);
        } catch (err) {
            showStatus('error', `Ошибка: ${err.message}`);
        } finally {
            setDetailLoading(false);
        }
    }, [accountId, expandedBatch, request, showStatus]);

    const checkTask = useCallback(async (taskId) => {
        try {
            const result = await request(`/api/v1/ozon-desc/${accountId}/check-task`, {
                method: 'POST',
                body: JSON.stringify({ task_id: taskId }),
            });
            setTaskResult({ taskId, data: result });
            showStatus('info', `Статус task ${taskId}: получен`);
        } catch (err) {
            showStatus('error', `Ошибка проверки: ${err.message}`);
        }
    }, [accountId, request, showStatus]);

    const revertBatch = useCallback(async (batchId) => {
        if (!window.confirm('Откатить изменения этого батча? Будут восстановлены оригинальные значения.')) {
            return;
        }
        setReverting(batchId);
        try {
            const result = await request(`/api/v1/ozon-desc/${accountId}/revert`, {
                method: 'POST',
                body: JSON.stringify({ batch_id: batchId }),
            });
            showStatus('success', `Откат отправлен: ${result.items_sent} товаров. Task IDs: ${(result.task_ids || []).join(', ')}`);
            loadHistory();
        } catch (err) {
            showStatus('error', `Ошибка отката: ${err.message}`);
        } finally {
            setReverting(null);
        }
    }, [accountId, request, showStatus, loadHistory]);

    // Export all history to CSV
    const exportAllHistory = useCallback(async () => {
        setExporting(true);
        try {
            const data = await request(`/api/v1/ozon-desc/${accountId}/export-history`);
            const items = data.items || [];

            if (items.length === 0) {
                showStatus('info', 'Нет данных для экспорта');
                return;
            }

            // Build CSV
            const headers = [
                'Дата', 'Пользователь', 'Артикул', 'SKU',
                'Было: Название', 'Стало: Название',
                'Было: Описание', 'Стало: Описание',
                'Rich изменён', 'Task ID', 'Статус'
            ];

            const escapeCSV = (val) => {
                if (val === null || val === undefined) return '';
                const str = String(val);
                if (str.includes(';') || str.includes('"') || str.includes('\n')) {
                    return '"' + str.replace(/"/g, '""') + '"';
                }
                return str;
            };

            const rows = items.map(item => [
                item.created_at ? fmtDateTime(item.created_at) : '',
                item.user_name || '',
                item.offer_id || '',
                item.sku || '',
                item.original_name || '',
                item.new_name || '',
                item.original_description || '',
                item.new_description || '',
                item.new_rich_content ? 'да' : 'нет',
                item.task_id || '',
                item.status || ''
            ].map(escapeCSV).join(';'));

            const csv = '﻿' + headers.join(';') + '\n' + rows.join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ozon_description_history_${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);

            showStatus('success', `Экспортировано ${items.length} записей`);
        } catch (err) {
            showStatus('error', `Ошибка экспорта: ${err.message}`);
        } finally {
            setExporting(false);
        }
    }, [accountId, request, showStatus]);

    // Open cell modal
    const openCellModal = (title, content) => {
        setCellModal({ title, content });
    };

    // Truncate text for table display
    const truncate = (text, maxLen = 100) => {
        if (!text) return null;
        return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
    };

    const totalPages = Math.max(1, Math.ceil(total / 20));

    return (
        <div className="desc-history">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontWeight: 700 }}>
                    История изменений описаний ({total})
                </h4>
                <button
                    className="desc-btn desc-btn--primary"
                    onClick={exportAllHistory}
                    disabled={exporting || total === 0}
                >
                    {exporting ? 'Экспорт...' : 'Скачать все логи (CSV)'}
                </button>
            </div>

            {loading && <div className="desc-loading">Загрузка...</div>}

            {!loading && batches.length === 0 && (
                <div className="desc-empty">Нет истории изменений</div>
            )}

            {batches.map((batch) => (
                <div key={batch.batch_id} className="desc-history__batch">
                    <div
                        className="desc-history__header"
                        onClick={() => loadBatchDetail(batch.batch_id)}
                    >
                        <span className="desc-history__date">
                            {batch.created_at ? fmtDateTime(batch.created_at) : '—'}
                        </span>
                        {batch.user && (
                            <span className="desc-history__user" title={batch.user.email}>
                                {batch.user.name}
                            </span>
                        )}
                        <span className="desc-history__count">
                            {batch.items_count} товаров
                        </span>
                        <span className="desc-history__statuses">
                            {(batch.statuses || []).map((s) => (
                                <span
                                    key={s}
                                    className={`desc-status desc-status--${s}`}
                                >
                                    {s}
                                </span>
                            ))}
                        </span>
                        {(batch.task_ids || []).map((tid) => (
                            <button
                                key={tid}
                                className="desc-btn desc-btn--small"
                                onClick={(e) => { e.stopPropagation(); checkTask(tid); }}
                            >
                                Task: {tid}
                            </button>
                        ))}
                        <button
                            className="desc-btn desc-btn--small desc-btn--revert"
                            disabled={reverting === batch.batch_id}
                            onClick={(e) => { e.stopPropagation(); revertBatch(batch.batch_id); }}
                        >
                            {reverting === batch.batch_id ? 'Откат...' : 'Отменить изменения'}
                        </button>
                        <span className="desc-history__expand">
                            {expandedBatch === batch.batch_id ? '▲' : '▼'}
                        </span>
                    </div>

                    {expandedBatch === batch.batch_id && (
                        <div className="desc-history__detail">
                            {detailLoading && <div className="desc-loading">Загрузка деталей...</div>}
                            {!detailLoading && batchDetail.length === 0 && (
                                <div className="desc-empty">Нет данных</div>
                            )}
                            {!detailLoading && batchDetail.length > 0 && (
                                <table className="desc-table desc-table--compact">
                                    <thead>
                                        <tr>
                                            <th className="desc-th desc-th--article">Артикул</th>
                                            <th className="desc-th desc-th--was">Было</th>
                                            <th className="desc-th desc-th--now">Стало</th>
                                            <th className="desc-th desc-th--rich">Rich</th>
                                            <th className="desc-th desc-th--status">Статус</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batchDetail.map((item) => {
                                            const wasContent = [
                                                item.original_name ? `Название: ${item.original_name}` : null,
                                                item.original_description ? `Описание: ${item.original_description}` : null,
                                            ].filter(Boolean).join('\n\n');

                                            const nowContent = [
                                                item.new_name ? `Название: ${item.new_name}` : null,
                                                item.new_description ? `Описание: ${item.new_description}` : null,
                                                item.new_rich_content ? `Rich Content: ${item.new_rich_content}` : null,
                                            ].filter(Boolean).join('\n\n');

                                            return (
                                                <tr key={item.id} className="desc-tr">
                                                    <td className="desc-td desc-td--article">{item.offer_id}</td>
                                                    <td
                                                        className="desc-td desc-td--old desc-td--clickable"
                                                        onClick={() => wasContent && openCellModal(`Было: ${item.offer_id}`, wasContent)}
                                                        title={wasContent ? 'Нажмите для просмотра' : ''}
                                                    >
                                                        {item.original_name && (
                                                            <div><strong>Название:</strong> {truncate(item.original_name)}</div>
                                                        )}
                                                        {item.original_description && (
                                                            <div style={{ marginTop: 4 }}><strong>Описание:</strong> {truncate(item.original_description)}</div>
                                                        )}
                                                        {!item.original_name && !item.original_description && '—'}
                                                    </td>
                                                    <td
                                                        className="desc-td desc-td--new desc-td--clickable"
                                                        onClick={() => nowContent && openCellModal(`Стало: ${item.offer_id}`, nowContent)}
                                                        title={nowContent ? 'Нажмите для просмотра' : ''}
                                                    >
                                                        {item.new_name && (
                                                            <div><strong>Название:</strong> {truncate(item.new_name)}</div>
                                                        )}
                                                        {item.new_description && (
                                                            <div style={{ marginTop: 4 }}><strong>Описание:</strong> {truncate(item.new_description)}</div>
                                                        )}
                                                        {!item.new_name && !item.new_description && (
                                                            <span style={{ color: '#999', fontStyle: 'italic' }}>не изменялось</span>
                                                        )}
                                                    </td>
                                                    <td className="desc-td" style={{ textAlign: 'center' }}>
                                                        {item.new_rich_content != null
                                                            ? <span className="desc-status desc-status--sent">да</span>
                                                            : <span style={{ color: '#999' }}>—</span>
                                                        }
                                                    </td>
                                                    <td className="desc-td" style={{ textAlign: 'center' }}>
                                                        <span className={`desc-status desc-status--${item.status}`}>
                                                            {item.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            ))}

            {taskResult && (
                <div className="desc-task-result">
                    <div className="desc-task-result__header">
                        <strong>Task {taskResult.taskId}</strong>
                        <button className="desc-result__close" onClick={() => setTaskResult(null)}>×</button>
                    </div>
                    <pre className="desc-task-result__json">
                        {JSON.stringify(taskResult.data, null, 2)}
                    </pre>
                </div>
            )}

            {/* Cell expand modal */}
            {cellModal && (
                <div className="desc-modal-overlay" onClick={() => setCellModal(null)}>
                    <div className="desc-modal desc-modal--cell" onClick={(e) => e.stopPropagation()}>
                        <div className="desc-modal__header">
                            <h3 style={{ margin: 0 }}>{cellModal.title}</h3>
                            <button className="desc-result__close" onClick={() => setCellModal(null)}>×</button>
                        </div>
                        <pre className="desc-modal__content">{cellModal.content}</pre>
                    </div>
                </div>
            )}

            {totalPages > 1 && (
                <div className="desc-pagination">
                    <button className="desc-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>←</button>
                    <span className="desc-pagination__info">{page} / {totalPages}</span>
                    <button className="desc-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>→</button>
                </div>
            )}
        </div>
    );
}

export default OzonDescriptionHistory;
